/**
 * Onglet JSON : lecture d'un texte collé ou édité à la main.
 *
 * Contrôle la FORME (types, champs présents) et ne garde que les champs
 * connus : Convex refuse tout champ en trop, et un champ manquant ferait
 * planter `validerScorecard`. Les règles de contenu restent celles de
 * `validerScorecard`, appliquées ensuite comme pour le formulaire.
 *
 * `followup` (relance) n'est ni écrit ni lu : c'est l'IA qui relance, comme
 * dans le formulaire. Un critère relu repart avec la relance qu'il avait.
 */

import type {
  CompetenceSaisie,
  ContenuScorecardSaisi,
  CritereSaisi,
  QuestionSaisie,
  SousCompetenceSaisie,
} from "@repo/ent-struct";

export type LectureJson =
  | { ok: true; contenu: ContenuScorecardSaisi }
  | { ok: false; erreur: string };

class FormeInvalide extends Error {}

type Objet = Record<string, unknown>;

function objet(valeur: unknown, chemin: string): Objet {
  if (typeof valeur !== "object" || valeur === null || Array.isArray(valeur)) {
    throw new FormeInvalide(`${chemin} : objet attendu`);
  }
  return valeur as Objet;
}

function texte(o: Objet, champ: string, chemin: string): string {
  const v = o[champ];
  if (typeof v !== "string") {
    throw new FormeInvalide(`${chemin}.${champ} : texte attendu`);
  }
  return v;
}

function nombre(o: Objet, champ: string, chemin: string): number {
  const v = o[champ];
  if (typeof v !== "number") {
    throw new FormeInvalide(`${chemin}.${champ} : nombre attendu`);
  }
  return v;
}

/** ID facultatif : absent = élément ajouté, l'ID sera attribué par le serveur. */
function idFacultatif(o: Objet, champ: string, chemin: string): { [k: string]: string } {
  if (o[champ] === undefined) return {};
  return { [champ]: texte(o, champ, chemin) };
}

function liste<T>(
  o: Objet,
  champ: string,
  chemin: string,
  lire: (v: unknown, chemin: string) => T,
): T[] {
  const v = o[champ];
  if (!Array.isArray(v)) {
    throw new FormeInvalide(`${chemin}.${champ} : liste attendue`);
  }
  return v.map((x, i) => lire(x, `${chemin}.${champ}[${i}]`));
}

function critere(v: unknown, chemin: string): CritereSaisi {
  const o = objet(v, chemin);
  return {
    ...idFacultatif(o, "criteriaId", chemin),
    description: texte(o, "description", chemin),
    points: nombre(o, "points", chemin),
    followup: "",
  };
}

/** `followup` de chaque critère existant, pour le rendre après lecture. */
function relancesDe(contenu: ContenuScorecardSaisi): Map<string, string> {
  const relances = new Map<string, string>();
  for (const c of contenu.competences) {
    const questions = [
      ...(c.questions ?? []),
      ...(c.sousCompetences ?? []).flatMap((s) => s.questions),
    ];
    for (const q of questions) {
      for (const k of q.criteres) {
        if (k.criteriaId) relances.set(k.criteriaId, k.followup);
      }
    }
  }
  return relances;
}

/**
 * Remet à chaque critère connu la relance qu'il avait dans `avant` : le texte
 * JSON ne la porte pas, la relire ne doit pas l'effacer.
 */
export function restituerRelances(
  contenu: ContenuScorecardSaisi,
  avant: ContenuScorecardSaisi,
): ContenuScorecardSaisi {
  const relances = relancesDe(avant);
  const critere = (k: CritereSaisi): CritereSaisi => ({
    ...k,
    followup: (k.criteriaId && relances.get(k.criteriaId)) || "",
  });
  const question = (q: QuestionSaisie): QuestionSaisie => ({
    ...q,
    criteres: q.criteres.map(critere),
  });
  return {
    ...contenu,
    competences: contenu.competences.map((c) => ({
      ...c,
      ...(c.questions && { questions: c.questions.map(question) }),
      ...(c.sousCompetences && {
        sousCompetences: c.sousCompetences.map((s) => ({
          ...s,
          questions: s.questions.map(question),
        })),
      }),
    })),
  };
}

function question(v: unknown, chemin: string): QuestionSaisie {
  const o = objet(v, chemin);
  return {
    ...idFacultatif(o, "questionId", chemin),
    type: texte(o, "type", chemin),
    texte: texte(o, "texte", chemin),
    criteres: liste(o, "criteres", chemin, critere),
  };
}

function sousCompetence(v: unknown, chemin: string): SousCompetenceSaisie {
  const o = objet(v, chemin);
  return {
    nom: texte(o, "nom", chemin),
    poids: nombre(o, "poids", chemin),
    questions: liste(o, "questions", chemin, question),
  };
}

function competence(v: unknown, chemin: string): CompetenceSaisie {
  const o = objet(v, chemin);
  return {
    nom: texte(o, "nom", chemin),
    poids: nombre(o, "poids", chemin),
    ...(o.questions !== undefined && {
      questions: liste(o, "questions", chemin, question),
    }),
    ...(o.sousCompetences !== undefined && {
      sousCompetences: liste(o, "sousCompetences", chemin, sousCompetence),
    }),
  };
}

export function lireContenuJson(source: string): LectureJson {
  let brut: unknown;
  try {
    brut = JSON.parse(source);
  } catch (e) {
    return { ok: false, erreur: `JSON invalide : ${(e as Error).message}` };
  }
  try {
    const o = objet(brut, "scorecard");
    return {
      ok: true,
      contenu: {
        titre: texte(o, "titre", "scorecard"),
        description: texte(o, "description", "scorecard"),
        competences: liste(o, "competences", "scorecard", competence),
      },
    };
  } catch (e) {
    if (e instanceof FormeInvalide) return { ok: false, erreur: e.message };
    throw e;
  }
}

/**
 * Texte de l'onglet. Repasse par la lecture pour l'ordre des champs : Convex
 * sert les clés par ordre alphabétique (`criteres` avant `questionId`), la
 * lecture les range dans l'ordre naturel (ID, type, texte, critères).
 */
export function ecrireContenuJson(contenu: ContenuScorecardSaisi): string {
  const lu = lireContenuJson(JSON.stringify(contenu));
  return JSON.stringify(
    lu.ok ? lu.contenu : contenu,
    (cle, valeur: unknown) => (cle === "followup" ? undefined : valeur),
    2,
  );
}
